<template>
  <div class="locale-switcher">
    <select v-model="selectedLocale" @change="changeLocale">
      <option :value="locale.code" v-for="locale in locales" :key="locale.code">
        {{ locale.name }}
      </option>
    </select>
  </div>
</template>

<script>
import { getSupportedLocales } from "@/i18n/supported-locales";
import { getDefaultLocaleByContext } from "@/utils/siteContext";
import { LOCALE_CHOSEN_KEY, isDefaultBrand } from "@/utils/brand";
import supportedLocales from "@/config/supported-locales";

/**
 * SEC-334: the languages this menu offers.
 *
 * Securdia's own sites hide a language by domain: Norwegian on the Swedish site, Polish on
 * the Norwegian one. A partner brand's address is neither of those sites, and the domain
 * check falls back to the current language, so a brand that starts in Polish or Norwegian
 * could find that very language missing here. The menu then had nothing to show for it and
 * stayed empty, and on its next draw it even reset the language to the site default.
 * On a brand's address every language is offered; Securdia's sites keep their rules.
 */
function languageChoices() {
  if (isDefaultBrand()) return getSupportedLocales();
  return Object.keys(supportedLocales).map((code) => ({ code, name: supportedLocales[code] }));
}

export default {
  data: () => ({
    locales: languageChoices(),
    selectedLocale: "",
  }),
  watch: {
    // SEC-334: a brand's starting language can arrive after this menu is drawn. On a
    // return visit the cached brand is applied first and the menu reads its value once;
    // the fresh answer from the API comes later and changes the portal's language. Follow
    // it here so the menu always shows the language the page is actually in. Picking a
    // language from the menu still works exactly as before (changeLocale).
    "$store.state.appLocale"(locale) {
      if (locale && locale !== this.selectedLocale) this.selectedLocale = locale;
    },
  },
  methods: {
    changeLocale(e) {
      this.selectedLocale = e.target.value;
      this.$i18n.locale = this.selectedLocale;
      this.$store.commit("SET_APP_LOCALE", this.selectedLocale);

      localStorage.setItem("securcloud_locale", this.selectedLocale);
      // SEC-334: a language picked here wins over a brand's starting language from now on
      localStorage.setItem(LOCALE_CHOSEN_KEY, "1");
    },
  },
  mounted() {
    const savedLocale = localStorage.getItem("securcloud_locale");
    const defaultLocale = getDefaultLocaleByContext();
    
    // Use saved locale if it exists and is available in current domain's locale list
    const availableLocaleCodes = this.locales.map(l => l.code);
    
    if (savedLocale && availableLocaleCodes.includes(savedLocale)) {
      this.selectedLocale = savedLocale;
    } else {
      // Use context-based default
      this.selectedLocale = defaultLocale;
      localStorage.setItem("securcloud_locale", defaultLocale);
    }
    
    this.$i18n.locale = this.selectedLocale;
    this.$store.commit("SET_APP_LOCALE", this.selectedLocale);
  },
};
</script>
